import { useState, useEffect } from "react";
import api from "../api";

import { FaLock, FaTrash } from "react-icons/fa";

export default function GenerateInvoiceModal({
    open,
    onClose,
    sejour
}) {

    const [loading, setLoading] = useState(false);

    const [loadingPrestations, setLoadingPrestations] =
        useState(false);

    const [lignes, setLignes] = useState([]);

    const [facture, setFacture] = useState(null);

    // ============================================================
    // CATALOGUE PRESTATIONS
    // ============================================================

    const [prestations, setPrestations] = useState([]);

    // ============================================================
    // RECHERCHE / INDEXATION DES CHAMPS
    // ============================================================

    const [rechercheType, setRechercheType] = useState({});
    const [rechercheDesignation, setRechercheDesignation] = useState({});
    const [typeOuvert, setTypeOuvert] = useState(null);
    const [designationOuverte, setDesignationOuverte] = useState(null);

    // ============================================================
    // CATÉGORIES DISPONIBLES
    // ============================================================

    // Les catégories affichées dans "Type" sont dérivées du catalogue
    // des prestations actives. Hébergement reste exclu.
    const categories = Array.from(
        new Map(
            prestations
                .map((prestation) => {
                    const type = getTypeService(
                        prestation.categorie_nom ||
                        prestation.categorie
                    );

                    return [
                        type,
                        getTypeServiceLabel(type)
                    ];
                })
                .filter(([type]) => type !== "autre")
        )
    ).map(([value, label]) => ({
        value,
        label
    }));

    // "Autre catégorie" reste toujours disponible, même sans prestation
    // correspondante dans le catalogue.
    if (!categories.some((categorie) => categorie.value === "autre")) {
        categories.push({
            value: "autre",
            label: "Autre catégorie"
        });
    }

    // ============================================================
    // DEVISE
    // ============================================================

    const [devise, setDevise] = useState("USD");

    const [tauxImmalta, setTauxImmalta] =
        useState(null);

    const [loadingTaux, setLoadingTaux] =
        useState(false);


    // ============================================================
    // CHARGEMENT DU CATALOGUE
    // ============================================================

    useEffect(() => {

        if (!open) return;

        const loadPrestations = async () => {

            try {

                setLoadingPrestations(true);

                const { data } =
                    await api.get(
                        "/prestations",
                        {
                            params: {
                                include_inactive: false
                            }
                        }
                    );

                const liste =
                    Array.isArray(data)
                        ? data
                        : Array.isArray(data?.data)
                            ? data.data
                            : [];

                // ====================================================
                // On exclut complètement Hébergement.
                //
                // L'hébergement est généré automatiquement à partir
                // du séjour et ne doit pas être sélectionné ici.
                // ====================================================

                const listeFiltree =
                    liste.filter((prestation) => {

                        const categorie =
                            String(
                                prestation.categorie_nom ||
                                prestation.categorie ||
                                ""
                            )
                                .trim()
                                .toLowerCase()
                                .normalize("NFD")
                                .replace(
                                    /[\u0300-\u036f]/g,
                                    ""
                                );

                        return (
                            prestation.actif !== false &&
                            categorie !== "hebergement"
                        );
                    });

                setPrestations(
                    listeFiltree
                );

            } catch (err) {

                console.error(
                    "Erreur chargement prestations :",
                    err
                );

                setPrestations([]);

                alert(
                    err.response?.data?.error ||
                    err.response?.data?.message ||
                    "Impossible de charger le catalogue des prestations."
                );

            } finally {

                setLoadingPrestations(false);
            }
        };

        loadPrestations();

    }, [open]);


    // ============================================================
    // CHARGEMENT FACTURE
    // ============================================================

    useEffect(() => {

        if (
            !open ||
            !sejour?.id
        ) {
            return;
        }

        const loadFacture = async () => {

            try {

                // ====================================================
                // Recherche d'une facture existante
                // ====================================================

                const { data } =
                    await api.get(
                        `/factures/sejour/${sejour.id}`
                    );

                setFacture(
                    data.facture
                );

                // ====================================================
                // Les lignes reçues du backend peuvent contenir
                // prestation_id.
                //
                // On les conserve telles quelles afin que la
                // modification d'une facture existante fonctionne.
                // ====================================================

                const lignesExistantes =
                    Array.isArray(data.lignes)
                        ? data.lignes.map(
                            (ligne) => ({
                                ...ligne,

                                quantite:
                                    Number(
                                        ligne.quantite || 0
                                    ),

                                prix_unitaire:
                                    Number(
                                        ligne.prix_unitaire || 0
                                    ),

                                montant:
                                    Number(
                                        ligne.montant || 0
                                    ),

                                prestation_id:
                                    ligne.prestation_id ??
                                    null
                            })
                        )
                        : [];

                setLignes(
                    lignesExistantes
                );

                // ====================================================
                // FACTURE EXISTANTE
                // ====================================================

                // La devise déjà enregistrée est conservée.
                setDevise(
                    data.facture?.devise ||
                    "USD"
                );

                setTauxImmalta(
                    data.facture?.devise === "CDF"
                        ? Number(
                            data.facture?.taux_change ||
                            0
                        )
                        : null
                );

            } catch (err) {

                // ====================================================
                // AUCUNE FACTURE
                // ====================================================

                if (
                    err.response?.status ===
                    404
                ) {

                    const dateDebut =
                        new Date(
                            sejour.date_debut
                        );

                    const dateFin =
                        new Date(
                            sejour.date_fin_prevue
                        );

                    const nbNuitees =
                        Math.max(
                            1,
                            Math.round(
                                (
                                    dateFin -
                                    dateDebut
                                ) /
                                (
                                    1000 *
                                    60 *
                                    60 *
                                    24
                                )
                            )
                        );

                    setFacture(null);

                    // Nouvelle facture = USD par défaut
                    setDevise("USD");

                    setTauxImmalta(null);

                    // =================================================
                    // Hébergement automatique
                    // =================================================

                    setLignes([
                        {
                            type_service:
                                "hebergement",

                            designation:
                                sejour.appartement_nom ||
                                sejour.numero ||
                                sejour.numero_appartement ||
                                sejour.appartement ||
                                "Hébergement",

                            quantite: 1,

                            nb_nuitees:
                                nbNuitees,

                            prix_unitaire:
                                Number(
                                    sejour.prix_nuitee_applique ||
                                    0
                                ),

                            montant:
                                Number(
                                    sejour.total_sejour ||
                                    0
                                ),

                            automatique: true,

                            prestation_id:
                                null
                        }
                    ]);

                } else {

                    console.error(err);

                    alert(
                        err.response?.data?.message ||
                        err.response?.data?.error ||
                        "Impossible de charger la facture."
                    );
                }
            }
        };

        loadFacture();

    }, [
        open,
        sejour
    ]);


    // ============================================================
    // RÉCUPÉRATION TAUX IMMALTA
    // ============================================================

    const chargerTauxImmalta =
        async () => {

            try {

                setLoadingTaux(true);

                const { data } =
                    await api.get(
                        "/currency/usd-cdf"
                    );

                if (
                    !data?.success ||
                    !Number(data.taux) ||
                    Number(data.taux) <= 0
                ) {

                    throw new Error(
                        "Taux USD/CDF Immalta invalide"
                    );
                }

                setTauxImmalta(
                    Number(data.taux)
                );

            } catch (err) {

                console.error(
                    "Erreur récupération taux Immalta :",
                    err
                );

                alert(
                    "Impossible de récupérer le taux USD/CDF Immalta."
                );

                setDevise("USD");

                setTauxImmalta(null);

            } finally {

                setLoadingTaux(false);
            }
        };


    // ============================================================
    // CHANGEMENT DE DEVISE
    // ============================================================

    const handleChangeDevise =
        async (nouvelleDevise) => {

            // ========================================================
            // FACTURE EXISTANTE
            // ========================================================

            if (facture) {

                // La devise d'une facture existante est figée.
                return;
            }

            setDevise(
                nouvelleDevise
            );

            if (
                nouvelleDevise === "CDF"
            ) {

                await chargerTauxImmalta();

            } else {

                setTauxImmalta(null);
            }
        };


    // ============================================================
    // GÉNÉRATION
    // ============================================================

    const handleGenerate =
        async () => {

            try {

                setLoading(true);

                // ====================================================
                // SÉCURITÉ CDF
                // ====================================================

                if (
                    devise === "CDF" &&
                    (
                        !tauxImmalta ||
                        !Number.isFinite(
                            Number(tauxImmalta)
                        ) ||
                        Number(tauxImmalta) <= 0
                    )
                ) {

                    throw new Error(
                        "Le taux USD/CDF Immalta est indisponible."
                    );
                }

                // ====================================================
                // VALIDATION DES PRESTATIONS
                // ====================================================

                const lignesAEnvoyer =
                    lignes
                        .filter(
                            (ligne) =>
                                !ligne.automatique
                        )
                        .map(
                            (ligne) => {

                                const resultat = {
                                    ...ligne
                                };

                                // ------------------------------------------------
                                // Une prestation du catalogue doit avoir
                                // prestation_id.
                                // ------------------------------------------------

                                if (
                                    ligne.prestation_id !==
                                        undefined &&
                                    ligne.prestation_id !==
                                        null &&
                                    String(
                                        ligne.prestation_id
                                    ).trim() !== ""
                                ) {

                                    resultat.prestation_id =
                                        Number(
                                            ligne.prestation_id
                                        );
                                }

                                return resultat;
                            }
                        );

                // ====================================================
                // APPEL API
                // ====================================================

                const res =
                    await api.post(
                        `/factures/generate/${sejour.id}`,
                        {
                            devise,

                            lignes:
                                lignesAEnvoyer
                        }
                    );

                console.log(
                    res.data
                );

                alert(
                    "Facture générée."
                );

                onClose();

            } catch (err) {

                console.error(err);

                alert(
                    err.response?.data?.message ||
                    err.response?.data?.error ||
                    err.message ||
                    "Erreur génération facture"
                );

            } finally {

                setLoading(false);
            }
        };


    // ============================================================
    // AJOUTER UNE LIGNE
    // ============================================================

    function ajouterLigne() {

        setLignes(
            (prev) => [
                ...prev,

                {
                    type_service:
                        "autre",

                    prestation_id:
                        null,

                    designation:
                        "",

                    quantite:
                        1,

                    nb_nuitees:
                        0,

                    prix_unitaire:
                        0,

                    montant:
                        0,

                    automatique:
                        false
                }
            ]
        );
    }


    // ============================================================
    // OUTILS DE RECHERCHE / INDEXATION
    // ============================================================

    function normaliserRecherche(valeur) {
        return String(valeur || "")
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\\u0300-\\u036f]/g, "")
            .trim();
    }

    function getPrestationsPourType(type) {
        return prestations.filter(
            (prestation) =>
                getTypeService(
                    prestation.categorie_nom ||
                    prestation.categorie
                ) === type
        );
    }

    function getCategoriesFiltrees(index) {
        const recherche = normaliserRecherche(
            rechercheType[index]
        );

        if (!recherche) {
            return categories;
        }

        return categories.filter((categorie) =>
            normaliserRecherche(categorie.label).includes(
                recherche
            )
        );
    }

    function getPrestationsFiltrees(index, type) {
        const recherche = normaliserRecherche(
            rechercheDesignation[index]
        );

        const liste = getPrestationsPourType(type);

        if (!recherche) {
            return liste;
        }

        return liste.filter((prestation) =>
            normaliserRecherche(
                prestation.nom_fr ||
                prestation.nom
            ).includes(recherche)
        );
    }

    // ============================================================
    // CHANGEMENT DE TYPE / CATÉGORIE
    // ============================================================

    function modifierTypeLigne(index, valeur) {
        setLignes((prev) => {
            const copie = [...prev];
            const ligne = {
                ...copie[index]
            };

            ligne.type_service = valeur || "autre";

            // Réinitialiser les recherches associées à cette ligne.
            setRechercheDesignation((prev) => ({
                ...prev,
                [index]: ""
            }));

            // Le changement de catégorie invalide la prestation
            // précédemment sélectionnée.
            ligne.prestation_id = null;
            ligne.designation = "";
            ligne.prix_unitaire = 0;
            ligne.montant = 0;

            copie[index] = ligne;
            return copie;
        });
    }

    // ============================================================
    // MODIFIER UNE LIGNE
    // ============================================================

    function modifierLigne(
        index,
        champ,
        valeur
    ) {

        setLignes(
            (prev) => {

                const copie =
                    [...prev];

                const ligne =
                    {
                        ...copie[index]
                    };

                // ====================================================
                // CHANGEMENT DE PRESTATION
                // ====================================================

                if (
                    champ ===
                    "prestation_id"
                ) {

                    if (
                        valeur === "" ||
                        valeur === null
                    ) {

                        ligne.prestation_id =
                            null;

                        ligne.type_service =
                            "autre";

                        ligne.designation =
                            "";

                        ligne.prix_unitaire =
                            0;

                    } else {

                        const prestation =
                            prestations.find(
                                (item) =>
                                    Number(
                                        item.id
                                    ) ===
                                    Number(
                                        valeur
                                    )
                            );

                        if (
                            prestation
                        ) {

                            ligne.prestation_id =
                                Number(
                                    prestation.id
                                );

                            // =================================================
                            // Le backend détermine aussi le type_service.
                            // On le renseigne ici pour l'affichage local.
                            // =================================================

                            ligne.type_service =
                                getTypeService(
                                    prestation.categorie_nom
                                );

                            ligne.designation =
                                prestation.nom_fr ||
                                prestation.nom ||
                                "";

                            setRechercheDesignation((prev) => ({
                                ...prev,
                                [index]:
                                    prestation.nom_fr ||
                                    prestation.nom ||
                                    ""
                            }));

                            // =================================================
                            // Prix catalogue comme prix initial.
                            //
                            // Le réceptionniste pourra ensuite le modifier.
                            // =================================================

                            ligne.prix_unitaire =
                                Number(
                                    prestation.prix_defaut ||
                                    0
                                );
                        }
                    }

                    const quantite =
                        Number(
                            ligne.quantite || 0
                        );

                    const prix =
                        Number(
                            ligne.prix_unitaire || 0
                        );

                    ligne.montant =
                        Math.round(
                            quantite *
                            prix *
                            100
                        ) / 100;

                    copie[index] =
                        ligne;

                    return copie;
                }

                // ====================================================
                // MODIFICATION NORMALE
                // ====================================================

                ligne[champ] =
                    valeur;

                const quantite =
                    Number(
                        champ === "quantite"
                            ? valeur
                            : ligne.quantite || 0
                    );

                const prix =
                    Number(
                        champ === "prix_unitaire"
                            ? valeur
                            : ligne.prix_unitaire || 0
                    );

                ligne.montant =
                    Math.round(
                        quantite *
                        prix *
                        100
                    ) / 100;

                copie[index] =
                    ligne;

                return copie;
            }
        );
    }


    // ============================================================
    // TYPE SERVICE POUR AFFICHAGE
    // ============================================================

    function getTypeService(
        categorieNom
    ) {

        const nom =
            String(
                categorieNom || ""
            )
                .trim()
                .toLowerCase()
                .normalize("NFD")
                .replace(
                    /[\u0300-\u036f]/g,
                    ""
                );

        switch (nom) {

            case "restauration":
                return "restauration";

            case "transport":
                return "transport";

            case "salle de conference":
                return "salle_conference";

            case "blanchisserie":
                return "blanchisserie";

            case "cafe & bar":
            case "cafe et bar":
            case "cafe-bar":
                return "cafe_bar";

            default:
                return "autre";
        }
    }


    // ============================================================
    // TOTAL
    // ============================================================

    const totalFacture =
        lignes.reduce(
            (
                total,
                ligne
            ) =>
                total +
                Number(
                    ligne.montant ||
                    0
                ),
            0
        );


    // ============================================================
    // TOTAL AFFICHÉ
    // ============================================================

    const totalAffiche =
        devise === "CDF" &&
        tauxImmalta
            ? totalFacture *
              tauxImmalta
            : totalFacture;


    // ============================================================
    // SUPPRIMER UNE LIGNE
    // ============================================================

    function supprimerLigne(
        index
    ) {

        setLignes(
            (prev) =>
                prev.filter(
                    (_, i) =>
                        i !== index
                )
        );
    }


    // ============================================================
    // FORMAT MONTANT
    // ============================================================

    function formatMontant(
        montant
    ) {

        return Number(
            montant || 0
        ).toLocaleString(
            "fr-FR",
            {
                minimumFractionDigits:
                    2,

                maximumFractionDigits:
                    2
            }
        );
    }


    // ============================================================
    // FERMETURE
    // ============================================================

    if (
        !open ||
        !sejour
    ) {
        return null;
    }


    // ============================================================
    // RENDER
    // ============================================================

    return (

        <div
            className="
                fixed
                inset-0
                bg-black/50
                flex
                items-center
                justify-center
                z-50
            "
        >

            <div
                className="
                    bg-white
                    rounded-2xl
                    shadow-2xl
                    w-[92vw]
                    max-w-5xl
                    max-h-[92vh]
                    flex
                    flex-col
                "
            >

                {/* ==================================================
                    EN-TÊTE
                ================================================== */}

                <div
                    className="
                        px-6
                        py-2
                        border-b
                        bg-white
                        rounded-t-2xl
                    "
                >

                    <h2
                        className="
                            text-2xl
                            font-bold
                            text-slate-800
                        "
                    >

                        {facture
                            ? `📝 Modification de la facture ${facture.numero}`
                            : "🧾 Génération de facture"}

                    </h2>

                </div>


                {/* ==================================================
                    INFORMATIONS SÉJOUR
                ================================================== */}

                <div
                    className="
                        px-6
                        py-2
                        bg-slate-50
                        border-b
                    "
                >

                    <div
                        className="
                            grid
                            grid-cols-2
                            md:grid-cols-4
                            gap-3
                            text-sm
                        "
                    >

                        <div>

                            <div
                                className="
                                    text-gray-500
                                "
                            >
                                Client
                            </div>

                            <div
                                className="
                                    font-semibold
                                "
                            >
                                {sejour.client_nom}
                            </div>

                        </div>


                        <div>

                            <div
                                className="
                                    text-gray-500
                                "
                            >
                                Appartement
                            </div>

                            <div
                                className="
                                    font-semibold
                                "
                            >
                                {
                                    sejour.appartement_nom ||
                                    sejour.numero
                                }
                            </div>

                        </div>


                        <div>

                            <div
                                className="
                                    text-gray-500
                                "
                            >
                                Séjour
                            </div>

                            <div
                                className="
                                    font-semibold
                                "
                            >
                                {sejour.date_debut}

                                {" → "}

                                {sejour.date_fin_prevue}
                            </div>

                        </div>


                        <div>

                            <div
                                className="
                                    text-gray-500
                                "
                            >
                                Facture
                            </div>

                            <div
                                className="
                                    font-semibold
                                    text-blue-700
                                "
                            >

                                {facture
                                    ? facture.numero
                                    : "Nouvelle facture"}

                            </div>

                        </div>

                    </div>


                    {/* ==================================================
                        DEVISE
                    ================================================== */}

                    <div
                        className="
                            mt-2
                        "
                    >

                        <div
                            className="
                                text-sm
                                font-semibold
                                text-slate-700
                                mb-1
                            "
                        >
                            Devise de facturation
                        </div>


                        <div
                            className="
                                flex
                                items-center
                                gap-4
                            "
                        >

                            <label
                                className={`
                                    flex
                                    items-center
                                    gap-2
                                    ${
                                        facture
                                            ? "cursor-not-allowed opacity-70"
                                            : "cursor-pointer"
                                    }
                                `}
                            >

                                <input
                                    type="radio"
                                    name="deviseFacture"
                                    value="USD"
                                    checked={
                                        devise === "USD"
                                    }
                                    disabled={
                                        !!facture
                                    }
                                    onChange={() =>
                                        handleChangeDevise(
                                            "USD"
                                        )
                                    }
                                />

                                <span
                                    className="
                                        font-medium
                                    "
                                >
                                    USD
                                </span>

                            </label>


                            <label
                                className={`
                                    flex
                                    items-center
                                    gap-2
                                    ${
                                        facture
                                            ? "cursor-not-allowed opacity-70"
                                            : "cursor-pointer"
                                    }
                                `}
                            >

                                <input
                                    type="radio"
                                    name="deviseFacture"
                                    value="CDF"
                                    checked={
                                        devise === "CDF"
                                    }
                                    disabled={
                                        !!facture
                                    }
                                    onChange={() =>
                                        handleChangeDevise(
                                            "CDF"
                                        )
                                    }
                                />

                                <span
                                    className="
                                        font-medium
                                    "
                                >
                                    CDF
                                </span>

                            </label>


                            {facture && (

                                <span
                                    className="
                                        text-xs
                                        text-gray-500
                                    "
                                >
                                    Devise figée sur la facture
                                </span>

                            )}

                        </div>


                        {/* ==================================================
                            TAUX IMMALTA
                        ================================================== */}

                        {devise === "CDF" && (

                            <div
                                className="
                                    mt-1
                                "
                            >

                                {loadingTaux ? (

                                    <div
                                        className="
                                            text-sm
                                            text-gray-500
                                        "
                                    >
                                        Récupération du taux Immalta...
                                    </div>

                                ) : tauxImmalta ? (

                                    <div
                                        className="
                                            inline-flex
                                            items-center
                                            gap-2
                                            px-3
                                            py-2
                                            rounded-lg
                                            bg-blue-50
                                            border
                                            border-blue-200
                                            text-sm
                                        "
                                    >

                                        <span
                                            className="
                                                text-gray-600
                                            "
                                        >
                                            Taux Immalta :
                                        </span>

                                        <strong
                                            className="
                                                text-blue-700
                                            "
                                        >
                                            1 USD ={" "}
                                            {formatMontant(
                                                tauxImmalta
                                            )}{" "}
                                            CDF
                                        </strong>

                                    </div>

                                ) : (

                                    <div
                                        className="
                                            text-sm
                                            text-red-600
                                        "
                                    >
                                        Taux USD/CDF indisponible.
                                    </div>

                                )}

                            </div>

                        )}

                    </div>

                </div>


                {/* ==================================================
                    PRESTATIONS
                ================================================== */}

                <div
                    className="
                        flex-1
                        overflow-y-auto
                        px-6
                        py-3
                    "
                >

                    <div
                        className="
                            flex
                            justify-between
                            items-center
                            mb-4
                        "
                    >

                        <h3
                            className="
                                text-lg
                                font-semibold
                            "
                        >

                            Prestations

                            <span
                                className="
                                    ml-2
                                    text-gray-500
                                    text-sm
                                "
                            >
                                ({lignes.length})
                            </span>

                        </h3>


                        <button
                            type="button"
                            onClick={
                                ajouterLigne
                            }
                            disabled={
                                loadingPrestations
                            }
                            className="
                                bg-emerald-600
                                hover:bg-emerald-700
                                disabled:opacity-50
                                disabled:cursor-not-allowed
                                text-white
                                px-4
                                py-2
                                rounded-lg
                                transition
                            "
                        >
                            + Ajouter une prestation
                        </button>

                    </div>


                    {loadingPrestations && (

                        <div
                            className="
                                mb-4
                                px-4
                                py-3
                                rounded-lg
                                bg-blue-50
                                border
                                border-blue-200
                                text-sm
                                text-blue-700
                            "
                        >
                            Chargement du catalogue des prestations...
                        </div>

                    )}


                    <div
                        className="
                            relative
                        "
                    >

                        <table
                            className="
                                min-w-full
                                text-sm
                                border-collapse
                            "
                        >

                            <thead
                                className="
                                    bg-slate-100
                                "
                            >

                                <tr>

                                    <th
                                        className="
                                            border
                                            px-3
                                            py-2
                                            font-semibold
                                            text-slate-700
                                        "
                                    >
                                        Type
                                    </th>

                                    <th
                                        className="
                                            border
                                            px-3
                                            py-2
                                            font-semibold
                                            text-slate-700
                                        "
                                    >
                                        Désignation
                                    </th>

                                    <th
                                        className="
                                            border
                                            px-3
                                            py-2
                                            font-semibold
                                            text-slate-700
                                        "
                                    >
                                        Qté
                                    </th>

                                    <th
                                        className="
                                            border
                                            px-3
                                            py-2
                                            font-semibold
                                            text-slate-700
                                        "
                                    >
                                        PU
                                    </th>

                                    <th
                                        className="
                                            border
                                            px-3
                                            py-2
                                            font-semibold
                                            text-slate-700
                                        "
                                    >
                                        Montant
                                    </th>

                                    <th
                                        className="
                                            border
                                            px-3
                                            py-2
                                            font-semibold
                                            text-slate-700
                                        "
                                    >
                                        Action
                                    </th>

                                </tr>

                            </thead>


                            <tbody className="relative z-0">

                                {lignes.map(
                                    (
                                        ligne,
                                        index
                                    ) => (

                                        <tr
                                            key={
                                                ligne.id ||
                                                `ligne-${index}`
                                            }
                                        >

                                            {/* ==================================================
                                                TYPE
                                            ================================================== */}

                                            <td
                                                className="
                                                    border
                                                    p-1
                                                    relative
                                                    z-20
                                                "
                                            >

                                                {ligne.automatique ? (

                                                    <span>
                                                        Hébergement
                                                    </span>

                                                ) : (

                                                    <div className="relative">
                                                        <input
                                                            type="text"
                                                            className="
                                                                w-full
                                                                rounded-md
                                                                border
                                                                border-gray-300
                                                                px-2
                                                                py-2
                                                                bg-white
                                                            "
                                                            placeholder="Rechercher un type..."
                                                            value={
                                                                typeOuvert === index
                                                                    ? (
                                                                        rechercheType[index] ??
                                                                        (
                                                                            ligne.type_service
                                                                                ? getTypeServiceLabel(
                                                                                    ligne.type_service
                                                                                )
                                                                                : ""
                                                                        )
                                                                    )
                                                                    : (
                                                                        ligne.type_service ? getTypeServiceLabel(ligne.type_service) : ""
                                                                    )
                                                            }
                                                            onFocus={() => {
                                                                setTypeOuvert(index);
                                                                setDesignationOuverte(null);
                                                            }}
                                                            onChange={(e) => {
                                                                setTypeOuvert(index);
                                                                setRechercheType((prev) => ({
                                                                    ...prev,
                                                                    [index]: e.target.value
                                                                }));
                                                            }}
                                                            disabled={
                                                                loadingPrestations
                                                            }
                                                        />

                                                        {typeOuvert === index && (
                                                            <div
                                                                className="
                                                                    absolute
                                                                    left-0
                                                                    right-0
                                                                    bottom-full
                                                                    mb-1
                                                                    z-[1000]
                                                                    max-h-64
                                                                    overflow-y-auto
                                                                    rounded-md
                                                                    border
                                                                    border-gray-300
                                                                    bg-white
                                                                    shadow-lg
                                                                "
                                                            >
                                                                {getCategoriesFiltrees(index).length > 0 ? (
                                                                    getCategoriesFiltrees(index).map(
                                                                        (categorie) => (
                                                                            <button
                                                                                key={
                                                                                    categorie.value
                                                                                }
                                                                                type="button"
                                                                                className="
                                                                                    block
                                                                                    w-full
                                                                                    px-3
                                                                                    py-2
                                                                                    text-left
                                                                                    text-sm
                                                                                    hover:bg-slate-100
                                                                                "
                                                                                onMouseDown={(e) =>
                                                                                    e.preventDefault()
                                                                                }
                                                                                onClick={() => {
                                                                                    modifierTypeLigne(
                                                                                        index,
                                                                                        categorie.value
                                                                                    );
                                                                                    setRechercheType(
                                                                                        (prev) => ({
                                                                                            ...prev,
                                                                                            [index]:
                                                                                                categorie.label
                                                                                        })
                                                                                    );
                                                                                    setTypeOuvert(null);
                                                                                    setDesignationOuverte(
                                                                                        null
                                                                                    );
                                                                                }}
                                                                            >
                                                                                {
                                                                                    categorie.label
                                                                                }
                                                                            </button>
                                                                        )
                                                                    )
                                                                ) : (
                                                                    <div className="px-3 py-2 text-sm text-gray-500">
                                                                        Aucun type trouvé
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>

                                                )}

                                            </td>


                                            {/* ==================================================
                                                PRESTATION
                                            ================================================== */}

                                            <td
                                                className="
                                                    border
                                                    p-1
                                                    min-w-[260px]
                                                    relative
                                                    z-20
                                                "
                                            >{ligne.automatique ? (

                                                    <span>
                                                        {
                                                            ligne.designation
                                                        }
                                                    </span>

                                                ) : (

                                                    <div className="relative">
                                                        <input
                                                            type="text"
                                                            className="
                                                                w-full
                                                                rounded-md
                                                                border
                                                                border-gray-300
                                                                px-2
                                                                py-2
                                                                bg-white
                                                            "
                                                            placeholder={
                                                                ligne.type_service === "autre"
                                                                    ? "Saisir librement la prestation..."
                                                                    : ligne.type_service
                                                                        ? "Rechercher une prestation..."
                                                                        : "Sélectionner d'abord un type"
                                                            }
                                                            value={
                                                                designationOuverte === index
                                                                    ? (
                                                                        rechercheDesignation[index] ??
                                                                        (
                                                                            ligne.designation || ""
                                                                        )
                                                                    )
                                                                    : (
                                                                        ligne.designation || ""
                                                                    )
                                                            }
                                                            onFocus={() => {
                                                                if (
                                                                    ligne.type_service === "autre"
                                                                ) {
                                                                    // Saisie libre : aucune prestation du catalogue
                                                                    // n'est nécessaire.
                                                                    setDesignationOuverte(null);
                                                                    setTypeOuvert(null);
                                                                    return;
                                                                }

                                                                if (ligne.type_service) {
                                                                    setDesignationOuverte(index);
                                                                    setTypeOuvert(null);
                                                                }
                                                            }}
                                                            onChange={(e) => {
                                                                if (ligne.type_service === "autre") {
                                                                    modifierLigne(
                                                                        index,
                                                                        "designation",
                                                                        e.target.value
                                                                    );
                                                                    setRechercheDesignation(
                                                                        (prev) => ({
                                                                            ...prev,
                                                                            [index]: e.target.value
                                                                        })
                                                                    );
                                                                    return;
                                                                }

                                                                if (!ligne.type_service) {
                                                                    return;
                                                                }

                                                                setDesignationOuverte(index);
                                                                setRechercheDesignation(
                                                                    (prev) => ({
                                                                        ...prev,
                                                                        [index]: e.target.value
                                                                    })
                                                                );
                                                            }}
                                                            disabled={
                                                                loadingPrestations ||
                                                                !ligne.type_service
                                                            }
                                                        />

                                                        {designationOuverte === index &&
                                                            ligne.type_service &&
                                                            ligne.type_service !== "autre" && (
                                                                <div
                                                                    className="
                                                                        absolute
                                                                        left-0
                                                                        right-0
                                                                        bottom-full
                                                                        mb-1
                                                                        z-[1000]
                                                                        max-h-72
                                                                        overflow-y-auto
                                                                        rounded-md
                                                                        border
                                                                        border-gray-300
                                                                        bg-white
                                                                        shadow-lg
                                                                    "
                                                                >
                                                                    {getPrestationsFiltrees(
                                                                        index,
                                                                        ligne.type_service
                                                                    ).length > 0 ? (
                                                                        getPrestationsFiltrees(
                                                                            index,
                                                                            ligne.type_service
                                                                        ).map(
                                                                            (
                                                                                prestation
                                                                            ) => (
                                                                                <button
                                                                                    key={
                                                                                        prestation.id
                                                                                    }
                                                                                    type="button"
                                                                                    className="
                                                                                        block
                                                                                        w-full
                                                                                        px-3
                                                                                        py-2
                                                                                        text-left
                                                                                        text-sm
                                                                                        hover:bg-slate-100
                                                                                    "
                                                                                    onMouseDown={(e) =>
                                                                                        e.preventDefault()
                                                                                    }
                                                                                    onClick={() => {
                                                                                        modifierLigne(
                                                                                            index,
                                                                                            "prestation_id",
                                                                                            prestation.id
                                                                                        );
                                                                                        setRechercheDesignation(
                                                                                            (prev) => ({
                                                                                                ...prev,
                                                                                                [index]:
                                                                                                    prestation.nom_fr ||
                                                                                                    prestation.nom
                                                                                            })
                                                                                        );
                                                                                        setDesignationOuverte(
                                                                                            null
                                                                                        );
                                                                                    }}
                                                                                >
                                                                                    <div className="font-medium text-slate-700">
                                                                                        {
                                                                                            prestation.nom_fr ||
                                                                                            prestation.nom
                                                                                        }
                                                                                    </div>
                                                                                    <div className="text-xs text-gray-500">
                                                                                        {
                                                                                            formatMontant(
                                                                                                prestation.prix_defaut
                                                                                            )
                                                                                        }{" "}
                                                                                        USD
                                                                                    </div>
                                                                                </button>
                                                                            )
                                                                        )
                                                                    ) : (
                                                                        <div className="px-3 py-2 text-sm text-gray-500">
                                                                            Aucune prestation trouvée
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                    </div>

                                                )}

                                            </td>


                                            {/* ==================================================
                                                QUANTITE
                                            ================================================== */}

                                            <td
                                                className="
                                                    border
                                                    p-1
                                                "
                                            >

                                                <input
                                                    type="number"
                                                    min="0.01"
                                                    step="0.01"
                                                    className="
                                                        w-full
                                                        rounded-md
                                                        border
                                                        border-gray-300
                                                        px-2
                                                        py-1
                                                        text-right
                                                        focus:ring-2
                                                        focus:ring-blue-500
                                                        focus:border-blue-500
                                                    "
                                                    value={
                                                        ligne.quantite
                                                    }
                                                    disabled={
                                                        ligne.automatique
                                                    }
                                                    onChange={
                                                        (e) =>
                                                            modifierLigne(
                                                                index,
                                                                "quantite",
                                                                e.target.value
                                                            )
                                                    }
                                                />

                                            </td>


                                            {/* ==================================================
                                                PRIX
                                            ================================================== */}

                                            <td
                                                className="
                                                    border
                                                    p-1
                                                "
                                            >

                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    className="
                                                        w-full
                                                        rounded-md
                                                        border
                                                        border-gray-300
                                                        px-2
                                                        py-1
                                                        bg-white
                                                        text-right
                                                    "
                                                    value={
                                                        ligne.prix_unitaire
                                                    }
                                                    disabled={
                                                        ligne.automatique
                                                    }
                                                    onChange={
                                                        (e) =>
                                                            modifierLigne(
                                                                index,
                                                                "prix_unitaire",
                                                                e.target.value
                                                            )
                                                    }
                                                />

                                            </td>


                                            {/* ==================================================
                                                MONTANT
                                            ================================================== */}

                                            <td
                                                className="
                                                    border
                                                    p-2
                                                    text-right
                                                "
                                            >

                                                <span
                                                    className="
                                                        font-semibold
                                                        text-slate-700
                                                    "
                                                >

                                                    {
                                                        formatMontant(
                                                            ligne.montant
                                                        )
                                                    }

                                                    {" "}

                                                    USD

                                                </span>

                                            </td>


                                            {/* ==================================================
                                                ACTION
                                            ================================================== */}

                                            <td
                                                className="
                                                    border
                                                    text-center
                                                "
                                            >

                                                {ligne.automatique ? (

                                                    <span
                                                        title="Ligne automatique"
                                                    >

                                                        <FaLock
                                                            size={14}
                                                            className="
                                                                text-gray-400
                                                            "
                                                        />

                                                    </span>

                                                ) : (

                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            supprimerLigne(
                                                                index
                                                            )
                                                        }
                                                        className="
                                                            text-red-600
                                                            hover:text-red-800
                                                        "
                                                        title="Supprimer"
                                                    >

                                                        <FaTrash
                                                            size={18}
                                                        />

                                                    </button>

                                                )}

                                            </td>

                                        </tr>

                                    )
                                )}

                            </tbody>

                        </table>

                    </div>


                    {!loadingPrestations &&
                        prestations.length === 0 && (

                            <div
                                className="
                                    mt-4
                                    p-4
                                    rounded-lg
                                    bg-amber-50
                                    border
                                    border-amber-200
                                    text-sm
                                    text-amber-800
                                "
                            >
                                Aucune prestation active n'est disponible dans le catalogue.
                                Le Comptable doit d'abord créer les prestations nécessaires.
                            </div>

                        )}

                </div>


                {/* ==================================================
                    PIED DE PAGE
                ================================================== */}

                <div
                    className="
                        border-t
                        bg-slate-50
                        px-6
                        py-2
                        flex
                        justify-between
                        items-center
                    "
                >

                    {/* ==================================================
                        RÉSUMÉ
                    ================================================== */}

                    <div
                        className="
                            bg-white
                            border
                            border-gray-200
                            rounded-xl
                            px-4
                            py-1.5
                            shadow-sm
                            min-w-[240px]
                        "
                    >

                        <div
                            className="
                                text-xs
                                uppercase
                                tracking-wide
                                text-gray-500
                            "
                        >

                            {lignes.length}
                            {" "}
                            prestation
                            {lignes.length > 1
                                ? "s"
                                : ""}

                        </div>


                        <div
                            className="
                                text-sm
                                text-gray-500
                                mt-0
                            "
                        >
                            Total facture
                        </div>


                        <div
                            className="
                                text-4xl
                                font-extrabold
                                text-blue-700
                            "
                        >

                            {
                                formatMontant(
                                    totalAffiche
                                )
                            }

                            {" "}

                            {devise}

                        </div>


                        {devise === "CDF" &&
                            tauxImmalta && (

                                <div
                                    className="
                                        text-xs
                                        text-gray-500
                                        mt-1
                                    "
                                >

                                    Équivalent :
                                    {" "}
                                    {
                                        formatMontant(
                                            totalFacture
                                        )
                                    }
                                    {" "}
                                    USD

                                </div>

                            )}

                    </div>


                    {/* ==================================================
                        BOUTONS
                    ================================================== */}

                    <div
                        className="
                            flex
                            gap-3
                        "
                    >

                        <button
                            type="button"
                            onClick={
                                onClose
                            }
                            className="
                                px-4
                                py-1.5
                                rounded-lg
                                bg-gray-300
                                hover:bg-gray-400
                                transition
                            "
                        >
                            Annuler
                        </button>


                        <button
                            type="button"
                            onClick={
                                handleGenerate
                            }
                            disabled={
                                loading ||
                                loadingTaux ||
                                loadingPrestations
                            }
                            className={`
                                px-5
                                py-1.5
                                rounded-lg
                                text-white
                                font-medium
                                transition
                                disabled:opacity-50
                                ${
                                    facture
                                        ? "bg-amber-600 hover:bg-amber-700"
                                        : "bg-blue-600 hover:bg-blue-700"
                                }
                            `}
                        >

                            {loading
                                ? facture
                                    ? "Enregistrement..."
                                    : "Génération..."
                                : facture
                                    ? "Enregistrer les modifications"
                                    : "Générer la facture"}

                        </button>

                    </div>

                </div>

            </div>

        </div>
    );
}


// ============================================================
// LIBELLÉ TYPE SERVICE
// ============================================================

function getTypeServiceLabel(
    type
) {

    switch (type) {

        case "restauration":
            return "Restauration";

        case "transport":
            return "Transport";

        case "salle_conference":
            return "Salle de conférence";

        case "blanchisserie":
            return "Blanchisserie";

        case "cafe_bar":
            return "Café & Bar";

        case "autre":
            return "Autre prestation";

        case "hebergement":
            return "Hébergement";

        default:
            return "Autre prestation";
    }
}